import { Injectable, signal } from '@angular/core';
import { ShipmentManagerState } from '../models/shipment-manager-state';
import { InventoryTabs } from '../models/inventory-tabs.enum';
import { EnrichedShipmentDetail, ShipmentDetails } from '../models/enrichedShipmentDetail';
import { Hub } from '@app/pages/hub-management/models/hub';
import { SelectedShipmentCache } from '../models/selected-shipment-cache';
import { ShipmentStatus } from '@app/shared/models/enums/shipment-status';
import { ShipmentCustomDateRange } from '../models/shipment-custom-date-range';
import { ShipmentInspectionSummaryDto } from '../models/shipment-inspection-summary-dto';

@Injectable({ providedIn: 'root' })
export class ShipmentManagerStateService {
  private state: ShipmentManagerState = {
    tabSelected: null,
    warehouseIdSelected: null,
    warehouses: [],
    shipments: [],
    gridFilterModel: null,
    selectedRowIds: [],
    selectedDateRange: null,
    customDateRange: { startDate: null, endDate: null },
    inspectionSummary: null
  };

  isCurrentWarehouseVendor = signal(false);
  isInboundLastMileShipment = signal(false);

  private selectedShipmentCache: SelectedShipmentCache | null = null;

  saveSelectedWarehouse(warehouseId: number) {
    this.state.warehouseIdSelected = warehouseId;
  }

  saveWarehousesState(warehouses: Hub[]) {
    this.state.warehouses = warehouses;
  }

  saveSelectedTab(tab: InventoryTabs) {
    this.state.tabSelected = tab;
  }

  saveShipments(shipments: EnrichedShipmentDetail[]) {
    this.state.shipments = shipments;
  }

  saveGridDataState(gridFilterModel: Record<string, any>, selectedRowIds: string[]) {
    this.state.gridFilterModel = gridFilterModel;
    this.state.selectedRowIds = selectedRowIds;
  }

  saveDateSelection(selectedDateRange: string, customDateRange: ShipmentCustomDateRange) {
    this.state.selectedDateRange = selectedDateRange;
    this.state.customDateRange = customDateRange;
  }

  getStateCopy(): ShipmentManagerState | null {
    return structuredClone(this.state);
  }

  clearState() {
    this.state = {
      tabSelected: null,
      warehouseIdSelected: null,
      warehouses: [],
      shipments: [],
      gridFilterModel: null,
      selectedRowIds: [],
      selectedDateRange: null,
      customDateRange: { startDate: null, endDate: null },
      inspectionSummary: null
    };
  }

  saveSelectedShipment(shipment: ShipmentDetails, allWarehouses: Hub[]): void {
    this.selectedShipmentCache = { shipment, allWarehouses };
  }

  consumeSelectedShipment(): SelectedShipmentCache | null {
    const cache = this.selectedShipmentCache;
    this.selectedShipmentCache = null;
    return cache;
  }

  updateCurrentWarehouseVendorFlag(value: boolean) {
    this.isCurrentWarehouseVendor.set(value);
  }

  updateInboundLastMileShipmentFlag(value: boolean) {
    this.isInboundLastMileShipment.set(value);
  }

  updateShipmentStatus(shipmentId: number, status: ShipmentStatus) {
    const shipment = this.state.shipments.find(s => s.id === shipmentId);
    if (shipment) {
      shipment.shipmentStatusId = status;
    }
  }

  updateShipment(updated: ShipmentDetails): void {
    const index = this.state.shipments.findIndex(s => s.id === updated.id);
    if (index !== -1) {
      this.state.shipments[index] = updated;
    }
  }

  getFirstItemDomainId(shipmentId: number): number | null {
    const shipment = this.state.shipments.find(s => s.id === shipmentId) ?? this.selectedShipmentCache?.shipment;
    const itemsByDomain = shipment?.itemsByDomain;
    if (!itemsByDomain) return null;
    const firstKey = Object.keys(itemsByDomain)[0];
    return firstKey != null ? Number(firstKey) : null;
  }

  saveInspectionSummary(inspectionSummary: ShipmentInspectionSummaryDto): void {
    this.state.inspectionSummary = inspectionSummary;
  }
}
